import type { ModelRobotAttachment } from '@app/api/RobotAttachments';
import type { MessageMode } from '@app/components/MessageInputWithFilesUpload/data/messageMode';
import type { ScheduleFormValues } from '@app/components/Schedule/types';
import type { NSalesFunnelsStagesApi } from '@app/services/rtkQuery/SalesFunnelsStages/types';
import type { MultiSelectValue } from '@app/shared/components/MultiSelect/types';
import type { NEmployees, NFunnels, NTags } from '@app/types';

import type { UploadFile } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import type { Dayjs } from 'dayjs';
import type { MutableRefObject } from 'react';

export interface BotDetailsFormInterface {
  trigger: string;
  deadline?: string;
  custom_deadline_day?: string;
  custom_deadline_time?: Dayjs;
  task_deadline?: string;
  task_custom_deadline_day?: string;
  task_custom_deadline_time?: Dayjs;
  funnelStage?: string;
  status?: string;
  services?: number[];
  serviceCategories?: number[];
  internalMovement?: boolean;
  searchPrompt?: string;
  scanPeriod?: number;
  comment?: string;
  afterExecutionTags: number[] | undefined;
  applyToExistingLeads?: boolean;
  tags?: MultiSelectValue | undefined;
  branch?: number | undefined;
  socialIntegrationType?: string | undefined;
  socialIntegrationId?: number | string | undefined;
  employees?: number[] | undefined;
  message?: string | undefined;
  buttons?: NSalesFunnelsStagesApi.RobotMessageButton[] | undefined;
  schedule: ScheduleFormValues['schedule'];
  files?: UploadFile[];
  socialType: string | undefined;
  useSchedule?: boolean;
  funnel?: number;
  isNeedRemovePrevFunnel?: boolean;
  description?: string;
  ownerId?: number | string;
  setTagIds?: number[];
  isNeedAdd?: boolean;
  conflictingTagIds?: number[];
  ignoreSchedule?: boolean;
  keywordGroups?: string[];
  isStopAfterFirstResponse?: boolean;
  commentReplyText?: string;
  publicationSelectionMode?: PublicationSelectionMode;
  instagramPublicationIds?: string[];
  messageMode?: MessageMode;
  aiPrompt?: string | undefined;
}

// Выбор публикаций Instagram у робота «Сообщение по ключевым словам»
// (task/11641, договор техлида от 24.10.2026). Значения — как в
// TypeParameters робота: 0 — все публикации (так читается и старый робот
// без поля), 1 — только выбранные.
export enum PublicationSelectionMode {
  All = 0,
  Selected = 1,
}

// Поля TypeParameters робота 6, PascalCase. В режиме All список — null.
export interface PublicationSelectionTypeParameters {
  PublicationSelectionMode: PublicationSelectionMode;
  InstagramPublicationIds: string[] | null;
}

// Публикация в сетке выбора — адаптер над InstagramPostDto
// (utils/instagramPublications.ts, mapInstagramPost).
export interface InstagramPublication {
  instagramId: string;
  isReels: boolean;
  typeLabel: string;
  previewUrl: string | null;
  caption: string | null;
  // Ссылка на публикацию в Instagram или null, если шлюз её не отдал
  permalink: string | null;
  // Дата выхода в формате DD.MM.YYYY или null
  publishedAtLabel: string | null;
}

// Разрешение проверки пересечений ключевых слов. Его держит хук модалки:
// isAllowed снимается в момент закрытия, до сброса формы, sessionId растёт
// при каждом открытии окна. Это ref, а не пропс: rc-dialog не
// перерисовывает содержимое закрытого окна, и новый пропс до блока проверки
// не доходит. Блок запоминает сессию, когда ставит проверку, и сверяет её
// в момент вызова и при ответе.
export interface OverlapCheckGate {
  isAllowed: boolean;
  sessionId: number;
}

export type OverlapCheckGateRef = Readonly<MutableRefObject<OverlapCheckGate>>;

// Строка предупреждения о пересечении кодовых слов (task/11645) — адаптер
// над ModelKeywordOverlap (utils/keywordOverlaps.ts, mapKeywordOverlapsResult).
export interface KeywordOverlap {
  key: string;
  // Готовый текст бэкенда: какое слово какое перекрывает
  message: string;
  otherRobotName: string;
  // «Воронка → этап» другого робота
  otherRobotLocation: string;
}

export interface KeywordOverlapsView {
  overlaps: KeywordOverlap[];
  // Сколько строк не пришло: бэкенд отдаёт не больше 100
  hiddenCount: number;
}

// Список тегов триггера TagIsSet («+ Добавить список»).
export interface RobotTagFilter {
  // Связка с предыдущими списками: true — И, false — ИЛИ.
  OuterInclude: boolean;
  // Условие внутри списка: true — И, false — ИЛИ, null — НЕТ.
  InnerInclude: boolean | null;
  TagIds: number[];
}

// Список тегов в TriggerInfo карточки робота — с готовыми подписями.
export interface RobotTagFilterDto extends RobotTagFilter {
  TagNames?: string[] | null;
}

export type BotDetailsType = 'create' | 'edit';

export interface BotTrigger {
  id: string;
  title: string;
  // Причина, по которой триггер нельзя выбрать (например, лимит на этап)
  disabledReason?: string;
}

export type CustomDeadlineTimeValue =
  | string
  | {
      format: (pattern: string) => string;
      hour: () => number;
      minute: () => number;
      second: () => number;
    }
  | undefined;

export type DeadlineFieldsProps = {
  title: string;
  deadlineFieldName: string;
  customDeadlineDayFieldName: string;
  customDeadlineTimeFieldName: string;
  selectedTriggerId?: string;
  selectedDeadlineItem: number | null;
  dropdownOpen: boolean;
  displayValue: string;
  dropdownContent: React.ReactNode;
  setDropdownOpen(open: boolean): void;
  handleDeadlineChange(value: number): void;
  handleDeadlineDropdownOpenChange(
    open: boolean,
    setOpen: React.Dispatch<boolean>,
    setTime: React.Dispatch<
      React.SetStateAction<{
        h: number;
        m: number;
        s: number;
      }>
    >,
    deadlineRawValue: CustomDeadlineTimeValue
  ): void;
  validateNumericInput(value: string): Promise<void>;
  onCustomDayInput: (
    e: React.FormEvent<HTMLInputElement>,
    fieldName: string
  ) => void;
  setTime: React.Dispatch<
    React.SetStateAction<{
      h: number;
      m: number;
      s: number;
    }>
  >;
  deadlineRawValue: CustomDeadlineTimeValue;
};

interface Props {
  title: string | JSX.Element;
  isOpen: boolean;
  onCancel(): void;
  onConfirm(values: BotDetailsFormInterface): void;
  initialValues?: {};
  isLoading?: boolean;
  triggerList: BotTrigger[];
  salesFunnelStagesList: Array<{ id: string; title: string }>;
  selectedBotTypeId?: string;
  servicesList: NEmployees.ModelClientService[];
  onDeleteBot(): void;
  isEditMode?: boolean;
  bot: {
    details: NSalesFunnelsStagesApi.GetRobotForIdResponse;
    bot: NFunnels.SalesFunnelRobotDto;
  } | null;
  type: BotDetailsType;
  employees: BaseOptionType[] | null;
  socialIntegrations: BaseOptionType[] | null;
  tags: NTags.Tag[];
  getTagsHandler: () => Promise<void>;
  allBranches: BaseOptionType[] | null;
  selectedBotFiles: ModelRobotAttachment[];
  salesFunnelsList: BaseOptionType[] | null;
  getSalesFunnelStagesListHandler: (
    customSalesFunnelId?: number
  ) => Promise<void>;
  isUploadingAttachments: boolean;
  taskUsersList: BaseOptionType[] | null;
}

export type BotDetailsModalType = (props: Props) => JSX.Element;

export interface UseBotDetailsModalProps {
  title: string | JSX.Element;
  onDeleteBot(): void;
  triggerList: BotTrigger[];
  onCancel(): void;
  isOpen: boolean;
  isLoading?: boolean;
  selectedBotTypeId?: string;
  socialIntegrations: BaseOptionType[] | null;
  bot: {
    details: NSalesFunnelsStagesApi.GetRobotForIdResponse;
    bot: NFunnels.SalesFunnelRobotDto;
  } | null;
  selectedBotFiles: ModelRobotAttachment[];
  getSalesFunnelStagesListHandler: (
    customSalesFunnelId?: number
  ) => Promise<void>;
  isUploadingAttachments: boolean;
}

export enum FormFields {
  CustomDeadlineDay = 'custom_deadline_day',
  CustomDeadlineTime = 'custom_deadline_time',
  Deadline = 'deadline',
}
